/**
 * SKI AXIS · /chapters/apply — Chapter Lead application form.
 *
 * 4-section institutional form: About You · Your Network · Why You · Commitments.
 * Submits to POST /api/chapters/applications.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, ArrowRight, Loader2, AlertCircle, ScrollText, MapPin,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.clap-root{background:#FAF7F0;min-height:100vh;color:#0D1B2A;font-family:'DM Sans',-apple-system,sans-serif;}
.clap-shell{max-width:980px;margin:0 auto;padding:48px 28px 96px;}
@media(max-width:680px){.clap-shell{padding:32px 18px 64px;}}

.clap-urgent{background:linear-gradient(90deg,#0D1B2A 0%,#1A2F4A 50%,#0D1B2A 100%);color:#FAF7F0;padding:14px 24px;text-align:center;font-size:11.5px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;border-bottom:2px solid #C8A84B;}
.clap-urgent em{color:#C8A84B;font-style:normal;font-weight:800;}

.clap-hero{padding:48px 0 32px;border-bottom:1px solid rgba(13,27,42,.08);margin-bottom:40px;}
.clap-eb{font-size:11px;font-weight:700;letter-spacing:.32em;color:#C8A84B;text-transform:uppercase;display:flex;align-items:center;gap:14px;margin-bottom:18px;}
.clap-eb::before{content:'';width:32px;height:1px;background:#C8A84B;}
.clap-h1{font-family:'Cormorant Display',serif;font-size:clamp(40px,5.5vw,62px);font-weight:700;line-height:1.02;letter-spacing:-.02em;margin:0 0 18px;color:#0D1B2A;}
.clap-h1 em{font-style:italic;color:#C8A84B;}
.clap-sub{font-size:17px;line-height:1.55;color:#3D4A5C;max-width:680px;margin:0;font-weight:300;}

.clap-form{background:#fff;border:1px solid #E8DFC9;padding:48px 56px;}
@media(max-width:680px){.clap-form{padding:32px 22px;}}
.clap-section{margin-bottom:40px;}
.clap-section:last-of-type{margin-bottom:0;}
.clap-sec-num{font-family:'Cormorant Display',serif;font-style:italic;color:#C8A84B;font-size:18px;font-weight:600;letter-spacing:.04em;}
.clap-sec-h{font-family:'Cormorant Display',serif;font-size:26px;font-weight:700;color:#0D1B2A;line-height:1.2;letter-spacing:-.01em;margin:6px 0 24px;border-bottom:1px solid #E8DFC9;padding-bottom:14px;}

.clap-row{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:18px;}
.clap-row-1{display:grid;grid-template-columns:1fr;gap:20px;margin-bottom:18px;}
@media(max-width:680px){.clap-row{grid-template-columns:1fr;}}

.clap-fld{display:flex;flex-direction:column;gap:6px;}
.clap-lbl{font-size:11px;font-weight:700;letter-spacing:.2em;color:#3D4A5C;text-transform:uppercase;}
.clap-lbl em{color:#C8A84B;font-style:normal;font-weight:800;}
.clap-help{font-size:13px;color:#7A8190;font-style:italic;font-family:'Cormorant Display',serif;}
.clap-input,.clap-select,.clap-area{font-family:inherit;font-size:14px;color:#0D1B2A;background:#FAF7F0;border:1px solid #E8DFC9;padding:13px 14px;outline:none;transition:border-color .15s;letter-spacing:.01em;}
.clap-input:focus,.clap-select:focus,.clap-area:focus{border-color:#C8A84B;background:#fff;box-shadow:0 0 0 3px rgba(200,168,75,.08);}
.clap-area{min-height:120px;resize:vertical;line-height:1.55;}
.clap-radios{display:flex;flex-wrap:wrap;gap:8px;}
.clap-radio{cursor:pointer;font-size:12px;font-weight:600;letter-spacing:.04em;color:#3D4A5C;background:#FAF7F0;border:1px solid #E8DFC9;padding:9px 16px;transition:all .15s;}
.clap-radio:hover{border-color:#C8A84B;color:#0D1B2A;}
.clap-radio input{display:none;}
.clap-radio.on{background:#0D1B2A;color:#C8A84B;border-color:#0D1B2A;}

.clap-submit{margin-top:32px;width:100%;background:#C8A84B;color:#0D1B2A;border:none;padding:18px 32px;font-family:inherit;font-size:13px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:10px;transition:all .2s;}
.clap-submit:hover:not(:disabled){background:#0D1B2A;color:#C8A84B;}
.clap-submit:disabled{opacity:.55;cursor:not-allowed;}
.clap-foot{margin-top:18px;text-align:center;font-size:13px;color:#7A8190;line-height:1.6;font-style:italic;font-family:'Cormorant Display',serif;font-size:14.5px;}
.clap-foot a{color:#C8A84B;text-decoration:none;font-style:normal;font-family:inherit;font-size:13px;font-weight:600;}
.clap-err{background:rgba(173,98,64,.08);border-left:3px solid #ad6240;color:#ad6240;padding:14px 18px;margin-bottom:20px;font-size:13px;display:flex;align-items:center;gap:10px;}

.clap-success{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:64px 56px;text-align:center;}
.clap-success-icn{display:inline-flex;width:64px;height:64px;border-radius:50%;background:rgba(200,168,75,.18);border:2px solid #C8A84B;color:#C8A84B;align-items:center;justify-content:center;margin-bottom:18px;}
.clap-success-h{font-family:'Cormorant Display',serif;font-size:42px;font-weight:700;color:#0D1B2A;line-height:1.05;letter-spacing:-.015em;margin:0 0 14px;}
.clap-success-h em{font-style:italic;color:#C8A84B;}
.clap-success-d{font-size:15px;color:#3D4A5C;line-height:1.65;max-width:560px;margin:0 auto 28px;}
.clap-success-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:14px 28px;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;}
.clap-success-cta:hover{background:#C8A84B;color:#0D1B2A;}
`;

const TERRITORIES = [
  'Dubai (HQ)', 'London', 'Amsterdam', 'Mumbai', 'Bangalore',
  'Riyadh', 'Doha', 'Singapore', 'Kuala Lumpur', 'Nairobi', 'Lagos',
  'Other (specify)',
];

const Radio = ({ name, value, label, current, onChange }) => (
  <label className={`clap-radio ${current === value ? 'on' : ''}`} data-testid={`clap-radio-${name}-${value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <input type="radio" name={name} value={value} checked={current === value} onChange={() => onChange(name, value)} />
    {label}
  </label>
);

const RadioGroup = ({ name, options, value, onChange }) => (
  <div className="clap-radios">
    {options.map((opt) => (
      <Radio
        key={typeof opt === 'string' ? opt : opt.value}
        name={name}
        value={typeof opt === 'string' ? opt : opt.value}
        label={typeof opt === 'string' ? opt : opt.label}
        current={value}
        onChange={onChange}
      />
    ))}
  </div>
);

export default function ChaptersApply() {
  useSEO({
    title: 'Apply · SKI Axis Chapter Lead Programme',
    description: 'Apply to lead the SKI Axis chapter in your city. 20% revenue share + 1% equity per founder placed. London, Amsterdam, Mumbai, Bangalore, Singapore and more.',
  });

  // Pre-select territory from ?territory=London style URL
  const initialTerritory = (() => {
    if (typeof window === 'undefined') return '';
    const t = (new URLSearchParams(window.location.search).get('territory') || '').trim();
    const match = TERRITORIES.find((x) => x.toLowerCase() === t.toLowerCase());
    return match || '';
  })();

  const [form, setForm] = useState({
    full_name: '',
    email: '',
    whatsapp: '',
    linkedin_url: '',
    territory: initialTerritory,
    city_of_residence: '',
    current_role: '',
    current_org: '',
    network_reach: '',
    prior_programme_experience: '',
    why_this_territory: '',
    why_ski_axis: '',
    can_commit_hours: '',
    can_run_local_workshops: '',
    can_attend_quarterly_calls: '',
    referred_by: '',
    additional_notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const updateRadio = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const required = [
    'full_name', 'email', 'whatsapp', 'linkedin_url', 'territory',
    'current_role', 'network_reach', 'why_this_territory', 'why_ski_axis',
    'can_commit_hours', 'can_run_local_workshops', 'can_attend_quarterly_calls',
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // OBSERVABILITY: fire-and-forget pre-validation attempt log so we have a
    // record of every Submit click — even when validation or network blocks
    // the real submission. The backend uses this to send abandoned-form
    // alerts after 5 minutes of no successful submission. (Feb 2026.)
    let attemptId = null;
    try {
      const attemptResp = await axios.post(`${API}/chapters/applications/attempt`, {
        ...form,
        page_url: typeof window !== 'undefined' ? window.location.href : null,
      }, { timeout: 8000 });
      attemptId = attemptResp?.data?.attempt_id || null;
    } catch (_ignored) {
      // Never block the real submission on the attempt log.
    }

    const missing = required.filter((k) => !String(form[k] || '').trim());
    if (missing.length) {
      // Tell the attempt log about the validation block so admins see *why*
      // an attempt didn't reach submission. Best-effort — silently ignored on error.
      if (attemptId) {
        axios.post(`${API}/chapters/applications/attempt`, {
          ...form,
          page_url: typeof window !== 'undefined' ? window.location.href : null,
          validation_errors: missing.join(', '),
        }, { timeout: 5000 }).catch(() => {});
      }
      setError(`Please complete the required fields: ${missing.join(', ').replace(/_/g, ' ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/chapters/applications`, { ...form, attempt_id: attemptId });
      setSubmitted({ id: data?.id, duplicate: !!data?.duplicate });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Submission failed. Please try again or email accelerator@skiasiapacific.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="clap-root" data-testid="chapter-apply-success">
        <style>{STYLES}</style>
        <Header />
        <div className="clap-shell">
          <div className="clap-success">
            <div className="clap-success-icn"><CheckCircle2 className="h-7 w-7" /></div>
            <h1 className="clap-success-h">Application <em>received</em>.</h1>
            <p className="clap-success-d">
              Thank you for applying to lead the SKI Axis chapter in <strong>{form.territory}</strong>. {submitted.duplicate ? "You already have an active application for this territory — we'll come back to you on the same timeline." : "Your application is logged. Shortlisted Chapter Lead candidates are invited to a 30-minute conversation with Dr. Neha Dewan."} You'll hear from us within <strong>10 working days</strong>. For urgent questions, email <a href="mailto:accelerator@skiasiapacific.com" style={{ color: '#C8A84B' }}>accelerator@skiasiapacific.com</a>.
            </p>
            <Link to="/chapters" className="clap-success-cta" data-testid="chapter-success-back">
              Back to programme overview <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="clap-root" data-testid="chapter-apply-page">
      <style>{STYLES}</style>
      <Header />

      <div className="clap-urgent" data-testid="chapter-urgency-bar">
        <em>Now appointing Chapter Leads</em> · 11 territories · One per city · Rolling review
      </div>

      <div className="clap-shell">
        {/* HERO */}
        <div className="clap-hero">
          <div className="clap-eb">SKI Axis Chapter Lead · Application</div>
          <h1 className="clap-h1">Lead the SKI Axis chapter in <em>your city</em>.</h1>
          <p className="clap-sub">
            Tell us about you, your local network, and why you're the right operator for this territory. Shortlisted
            applicants meet with Dr. Neha Dewan and Dr. Ross Rafahël before an offer is made.
          </p>
        </div>

        <form onSubmit={onSubmit} className="clap-form" data-testid="chapter-apply-form">
          {error && (
            <div className="clap-err" data-testid="chapter-form-error">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* SECTION 1 — ABOUT YOU */}
          <div className="clap-section">
            <div className="clap-sec-num">i.</div>
            <h2 className="clap-sec-h">About you</h2>
            <div className="clap-row">
              <div className="clap-fld">
                <label className="clap-lbl">Full name <em>*</em></label>
                <input className="clap-input" data-testid="clap-full_name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Email <em>*</em></label>
                <input type="email" className="clap-input" data-testid="clap-email" value={form.email} onChange={(e) => update('email', e.target.value)} required />
              </div>
            </div>
            <div className="clap-row">
              <div className="clap-fld">
                <label className="clap-lbl">WhatsApp number <em>*</em></label>
                <input className="clap-input" placeholder="+44 ..." data-testid="clap-whatsapp" value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} required />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">LinkedIn URL <em>*</em></label>
                <input className="clap-input" placeholder="https://linkedin.com/in/..." data-testid="clap-linkedin_url" value={form.linkedin_url} onChange={(e) => update('linkedin_url', e.target.value)} required />
              </div>
            </div>
            <div className="clap-row">
              <div className="clap-fld">
                <label className="clap-lbl">Current role / title <em>*</em></label>
                <input className="clap-input" placeholder="Founder, Partner, Director..." data-testid="clap-current_role" value={form.current_role} onChange={(e) => update('current_role', e.target.value)} required />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Current organisation</label>
                <input className="clap-input" data-testid="clap-current_org" value={form.current_org} onChange={(e) => update('current_org', e.target.value)} />
              </div>
            </div>
            <div className="clap-row">
              <div className="clap-fld">
                <label className="clap-lbl">City of residence</label>
                <input className="clap-input" data-testid="clap-city_of_residence" value={form.city_of_residence} onChange={(e) => update('city_of_residence', e.target.value)} />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Territory you want to lead <em>*</em></label>
                <select className="clap-select" data-testid="clap-territory" value={form.territory} onChange={(e) => update('territory', e.target.value)} required>
                  <option value="">Select territory…</option>
                  {TERRITORIES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* SECTION 2 — YOUR NETWORK */}
          <div className="clap-section">
            <div className="clap-sec-num">ii.</div>
            <h2 className="clap-sec-h">Your local network</h2>
            <div className="clap-row-1">
              <div className="clap-fld">
                <label className="clap-lbl">Describe your founder + investor network in this territory <em>*</em></label>
                <span className="clap-help">300 words max — depth and reach, not name-dropping</span>
                <textarea className="clap-area" data-testid="clap-network_reach" maxLength={2400} value={form.network_reach} onChange={(e) => update('network_reach', e.target.value)} required />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Have you run cohorts or programmes before?</label>
                <span className="clap-help">FI chapter, Endeavor, Techstars, sector communities, angel networks, etc.</span>
                <textarea className="clap-area" data-testid="clap-prior_programme_experience" maxLength={1800} value={form.prior_programme_experience} onChange={(e) => update('prior_programme_experience', e.target.value)} />
              </div>
            </div>
          </div>

          {/* SECTION 3 — WHY YOU */}
          <div className="clap-section">
            <div className="clap-sec-num">iii.</div>
            <h2 className="clap-sec-h">Why you</h2>
            <div className="clap-row-1">
              <div className="clap-fld">
                <label className="clap-lbl">Why this territory? <em>*</em></label>
                <span className="clap-help">What unique advantage do you have here that another applicant likely does not?</span>
                <textarea className="clap-area" data-testid="clap-why_this_territory" maxLength={1800} value={form.why_this_territory} onChange={(e) => update('why_this_territory', e.target.value)} required />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Why SKI Axis specifically? <em>*</em></label>
                <span className="clap-help">Governance-first accelerator vs. generalist accelerators — what attracts you?</span>
                <textarea className="clap-area" data-testid="clap-why_ski_axis" maxLength={1800} value={form.why_ski_axis} onChange={(e) => update('why_ski_axis', e.target.value)} required />
              </div>
            </div>
          </div>

          {/* SECTION 4 — COMMITMENTS */}
          <div className="clap-section">
            <div className="clap-sec-num">iv.</div>
            <h2 className="clap-sec-h">Commitments</h2>
            <div className="clap-row-1">
              <div className="clap-fld">
                <label className="clap-lbl">Hours per week you can commit <em>*</em></label>
                <RadioGroup name="can_commit_hours" options={['10-15', '15-20', '20+', 'Varies']} value={form.can_commit_hours} onChange={updateRadio} />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Can you run 4–6 local workshops per year? <em>*</em></label>
                <RadioGroup name="can_run_local_workshops" options={['Yes', 'No', 'Maybe']} value={form.can_run_local_workshops} onChange={updateRadio} />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Can you attend quarterly accountability calls (online)? <em>*</em></label>
                <RadioGroup name="can_attend_quarterly_calls" options={['Yes', 'No']} value={form.can_attend_quarterly_calls} onChange={updateRadio} />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Who referred you? (optional)</label>
                <input className="clap-input" placeholder="Name + email if applicable" data-testid="clap-referred_by" value={form.referred_by} onChange={(e) => update('referred_by', e.target.value)} />
              </div>
              <div className="clap-fld">
                <label className="clap-lbl">Anything else we should know?</label>
                <textarea className="clap-area" data-testid="clap-additional_notes" maxLength={2400} value={form.additional_notes} onChange={(e) => update('additional_notes', e.target.value)} />
              </div>
            </div>
          </div>

          <button type="submit" className="clap-submit" disabled={submitting} data-testid="chapter-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScrollText className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit my Chapter Lead application'}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>

          <p className="clap-foot">
            Reviewed on a rolling basis · Shortlisted candidates meet Dr. Neha Dewan within 10 working days · 60-day notice from initial appointment.<br />
            Questions: <a href="mailto:accelerator@skiasiapacific.com">accelerator@skiasiapacific.com</a>
          </p>
        </form>
      </div>

      <Footer />
    </div>
  );
}
